import { useEffect, useRef, useState } from "react";
import { useAppDialog } from "../../../components/AppDialogProvider";
import { formatImportErrorMessage } from "../../../utils/importErrorMessage";
import { buildApiRequestHeaders } from "../../../api/client";

function buildRequestOptions(getRequestOptions) {
  const requestOptions = getRequestOptions ? (getRequestOptions() || {}) : {};
  const method = requestOptions.method || "GET";

  return {
    ...requestOptions,
    cache: "no-store",
    credentials: requestOptions.credentials || "include",
    headers: buildApiRequestHeaders(method, requestOptions.headers)
  };
}

export function useCsvImport({
  uploadUrl,
  getStatusUrl,
  onCompleted,
  getRequestOptions,
  pollingIntervalMs = 250,
}) {
  const { showAlert } = useAppDialog();
  const [file, setFile] = useState(null);
  const [importStatus, setImportStatus] = useState(null);
  const fileInputRef = useRef(null);
  const getStatusUrlRef = useRef(getStatusUrl);
  const onCompletedRef = useRef(onCompleted);
  const getRequestOptionsRef = useRef(getRequestOptions);

  useEffect(() => {
    getStatusUrlRef.current = getStatusUrl;
  }, [getStatusUrl]);

  useEffect(() => {
    onCompletedRef.current = onCompleted;
  }, [onCompleted]);

  useEffect(() => {
    getRequestOptionsRef.current = getRequestOptions;
  }, [getRequestOptions]);

  useEffect(() => {
    if (!importStatus?.importId) {
      return undefined;
    }

    let timeoutId = null;
    let isDisposed = false;

    const stopPolling = () => {
      if (timeoutId) {
        window.clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const scheduleNextPoll = () => {
      if (isDisposed) {
        return;
      }

      timeoutId = window.setTimeout(() => {
        void pollStatus();
      }, pollingIntervalMs);
    };

    const pollStatus = async () => {
      try {
        const res = await fetch(
          getStatusUrlRef.current(importStatus.importId),
          buildRequestOptions(getRequestOptionsRef.current)
        );

        if (!res.ok) {
          throw new Error("Could not fetch import status");
        }

        const status = await res.json();
        setImportStatus(status);

        if (status.stage === "completed") {
          stopPolling();
          setFile(null);

          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }

          if (onCompletedRef.current) {
            onCompletedRef.current(status);
          }
        } else if (status.stage === "failed") {
          stopPolling();
        } else {
          scheduleNextPoll();
        }
      } catch (err) {
        stopPolling();
        console.error("Import status error:", err);
        setImportStatus((current) => current ? {
          ...current,
          stage: "failed",
          error: err.message || "Could not fetch import status"
        } : current);
      }
    };

    void pollStatus();

    return () => {
      isDisposed = true;
      stopPolling();
    };
  }, [importStatus?.importId, pollingIntervalMs]);

  const handleUpload = async () => {
    if (!file) {
      await showAlert({
        title: "CSV Import",
        message: "Select a CSV file first"
      });
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);

      setImportStatus({
        importId: null,
        stage: "uploading",
        totalRows: 0,
        processedRows: 0,
        importedRows: 0,
        error: null
      });

      const res = await fetch(uploadUrl, {
        ...buildRequestOptions(getRequestOptionsRef.current),
        method: "POST",
        headers: buildApiRequestHeaders("POST"),
        body: formData
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || !json.importId) {
        throw new Error(formatImportErrorMessage(json, "Upload failed"));
      }

      setImportStatus({
        importId: json.importId,
        stage: "queued",
        totalRows: 0,
        processedRows: 0,
        importedRows: 0,
        error: null
      });
    } catch (err) {
      console.error("Upload error:", err);
      setImportStatus({
        importId: null,
        stage: "failed",
        totalRows: 0,
        processedRows: 0,
        importedRows: 0,
        error: err.message || "Upload failed"
      });
    }
  };

  const resetImportState = () => {
    setFile(null);
    setImportStatus(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const totalImportRows = importStatus?.totalRows || 0;
  const processedImportRows = importStatus?.processedRows || 0;
  const isImportActive = ["uploading", "queued", "processing"].includes(importStatus?.stage || "");
  const isImportPreparing = isImportActive && totalImportRows === 0;
  const showDeterminateImportProgress = totalImportRows > 0 || importStatus?.stage === "completed" || importStatus?.stage === "failed";
  const importProgressPercent = totalImportRows > 0
    ? Math.min(100, Math.round((processedImportRows / totalImportRows) * 100))
    : importStatus?.stage === "completed"
      ? 100
      : 0;
  const importStatusLabel = importStatus?.stage === "uploading"
    ? "Uploading CSV..."
    : importStatus?.stage === "queued"
      ? "Import is queued..."
      : importStatus?.stage === "processing"
        ? totalImportRows > 0
          ? `${processedImportRows} / ${totalImportRows} rows processed...`
          : "Preparing import..."
        : importStatus?.stage === "completed"
          ? `Import finished. ${importStatus.importedRows || 0} cards were imported.`
          : importStatus?.stage === "failed"
            ? importStatus.error || "Import failed"
            : null;

  return {
    file,
    setFile,
    fileInputRef,
    importStatus,
    handleUpload,
    resetImportState,
    totalImportRows,
    isImportActive,
    isImportPreparing,
    showDeterminateImportProgress,
    importProgressPercent,
    importStatusLabel,
  };
}